import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";

import api from "../../api/axios";
import AppModalShell from "../common/AppModalShell";
import { getSearchKeyIcon } from "../../constants/searchKeys";
import { buildEventPath } from "../../utils/eventUrl";
import { toPublicWebUrl } from "../../native/runtime";


const TASK_ICONS = {
  greeting: "👋",
  departure: "🛣️",
  arrival_photo: "📸",
  meetup: "🔑",
};

const formatTaskReward = (reward, t) => {
  if (reward?.kind === "key") {
    return t("welcome_crew.reward_key", {
      level: Number(reward?.level || 3),
      defaultValue: `Level ${Number(reward?.level || 3)} key`,
    });
  }
  return t("welcome_crew.reward_coins", {
    count: Number(reward?.amount || 0),
    defaultValue: `+${Number(reward?.amount || 0)} coins`,
  });
};

const formatCompactRewardAmount = (value) => {
  const numeric = Math.max(0, Math.floor(Number(value) || 0));
  if (numeric >= 1e9) return `${(numeric / 1e9).toFixed(1).replace(/\.0$/, "")}B`;
  if (numeric >= 1e6) return `${(numeric / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
  if (numeric >= 1e3) return `${(numeric / 1e3).toFixed(1).replace(/\.0$/, "")}K`;
  return String(numeric);
};

const TaskRewardValue = ({ reward, t }) => {
  if (reward?.kind === "coins") {
    const amount = Number(reward?.amount || 0);
    return (
      <span
        className="inline-flex items-center gap-1"
        aria-label={formatTaskReward(reward, t)}
      >
        <span>+{formatCompactRewardAmount(amount)}</span>
        <img
          src="/assets/vault/coin_icon.svg"
          alt=""
          aria-hidden="true"
          className="h-4 w-4 shrink-0"
        />
      </span>
    );
  }
  if (reward?.kind === "key") {
    const level = Number(reward?.level || 3);
    return (
      <span
        className="inline-flex items-center gap-1"
        aria-label={formatTaskReward(reward, t)}
      >
        <span>+{Math.max(1, Number(reward?.amount || 1))}</span>
        <img
          src={getSearchKeyIcon(level)}
          alt=""
          aria-hidden="true"
          className="h-6 w-6 shrink-0 rotate-[45deg]"
        />
      </span>
    );
  }
  return <>{formatTaskReward(reward, t)}</>;
};

const formatOpenTime = (value, language) => {
  const parsed = new Date(value || "");
  if (Number.isNaN(parsed.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(language || undefined, {
      hour: "2-digit",
      minute: "2-digit",
    }).format(parsed);
  } catch {
    return "";
  }
};

export default function WelcomeCrewPanel({
  state,
  chatId,
  onStateChange,
  onPickPhoto,
}) {
  const { t, i18n } = useTranslation("menu");
  const navigate = useNavigate();
  const qrCanvasRef = useRef(null);
  const [expanded, setExpanded] = useState(true);
  const [qrPath, setQrPath] = useState("");
  const [qrLoading, setQrLoading] = useState(false);
  const [gameLoading, setGameLoading] = useState(false);
  const [claimingTaskId, setClaimingTaskId] = useState("");

  const currentTask = String(state?.current_task || "");
  const tasks = Array.isArray(state?.tasks) ? state.tasks : [];
  const activeTask = tasks.find((item) => item?.status === "current") || null;
  const activeTaskNeedsReward = Boolean(activeTask?.claimable && !activeTask?.claimed);
  const completedCount = tasks.filter(
    (item) => item?.status === "done" || (item?.status === "current" && item?.claimable)
  ).length;
  const departureOpenLabel = formatOpenTime(
    state?.departure_valid_from,
    i18n.resolvedLanguage || i18n.language
  );
  const meetupMembers = Array.isArray(state?.members) ? state.members : [];
  const confirmedMeetupCount = meetupMembers.filter((member) => member?.meetup_confirmed).length;
  const question = state?.game?.question || null;
  const questionText = useMemo(() => {
    const language = String(i18n.resolvedLanguage || i18n.language || "en").toLowerCase();
    if (language.startsWith("sk")) return question?.question_sk || question?.question_en || "";
    return question?.question_en || question?.question_sk || "";
  }, [i18n.language, i18n.resolvedLanguage, question]);

  useEffect(() => {
    if (!qrPath || !qrCanvasRef.current) return;
    const absoluteUrl = toPublicWebUrl(qrPath);
    QRCode.toCanvas(qrCanvasRef.current, absoluteUrl, {
      width: 280,
      margin: 2,
      color: { dark: "#081329", light: "#FFFFFF" },
    }).catch(() => setQrPath(""));
  }, [qrPath]);

  if (!state || state?.kind !== "welcome_crew") return null;

  const showQr = async () => {
    if (!chatId || qrLoading) return;
    setQrLoading(true);
    try {
      const response = await api.post(`/chats/groups/${chatId}/buddy-qr-token/`);
      setQrPath(String(response?.data?.claim_path || ""));
    } catch (error) {
      const message = error?.response?.data?.error || t("welcome_crew.qr_failed", {
        defaultValue: "Could not create the meetup QR.",
      });
      window.dispatchEvent(new CustomEvent("global-error", { detail: { message } }));
      if (error?.response?.data?.buddy_state) {
        onStateChange?.(error.response.data.buddy_state);
      }
    } finally {
      setQrLoading(false);
    }
  };

  const nextQuestion = async () => {
    if (!chatId || gameLoading) return;
    setGameLoading(true);
    try {
      const response = await api.post(`/chats/groups/${chatId}/buddy-game-next/`);
      onStateChange?.(response?.data?.buddy_state || state);
    } catch (error) {
      const message = error?.response?.data?.error || t("welcome_crew.game_failed", {
        defaultValue: "Could not load the next question.",
      });
      window.dispatchEvent(new CustomEvent("global-error", { detail: { message } }));
    } finally {
      setGameLoading(false);
    }
  };

  const claimReward = async (task) => {
    const taskId = String(task?.id || "");
    if (!chatId || !taskId || !task?.claimable || claimingTaskId) return;
    setClaimingTaskId(taskId);
    try {
      const response = await api.post(`/chats/groups/${chatId}/buddy-reward/claim/`, {
        task_id: taskId,
      });
      onStateChange?.(response?.data?.buddy_state || state);
      if (task?.reward?.kind === "coins") {
        window.dispatchEvent(new CustomEvent("vg-vault-coins-collected"));
      }
    } catch (error) {
      const message = error?.response?.data?.error || t("welcome_crew.claim_failed", {
        defaultValue: "Could not collect this reward.",
      });
      window.dispatchEvent(new CustomEvent("global-error", { detail: { message } }));
      if (error?.response?.data?.buddy_state) {
        onStateChange?.(error.response.data.buddy_state);
      }
    } finally {
      setClaimingTaskId("");
    }
  };

  const openAttendance = () => {
    const eventId = Number(state?.event?.id || 0);
    if (!eventId) return;
    navigate(`${buildEventPath(eventId, state?.event?.name || "")}?confirm=1`);
  };

  const openSession = () => {
    const eventId = Number(state?.event?.id || 0);
    if (!eventId) return;
    navigate(String(state?.event?.session_path || `/events/${eventId}/party-session`));
  };

  return (
    <section className="mb-4 overflow-hidden rounded-[24px] border border-cyan-300/25 bg-[linear-gradient(145deg,rgba(23,93,182,0.96),rgba(10,30,70,0.98))] shadow-[0_18px_40px_rgba(0,0,0,0.3)]">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-cyan-300 text-[20px]">{TASK_ICONS[currentTask] || "🎉"}</span>
        <span className="min-w-0 flex-1">
          <span className="block text-[10px] font-black uppercase tracking-[0.14em] text-cyan-200">
            {t("welcome_crew.eyebrow", { defaultValue: "Welcome Crew mission" })}
          </span>
          <span className="mt-0.5 block truncate text-[15px] font-bold text-white">
            {currentTask === "complete"
              ? t("welcome_crew.complete_title", { defaultValue: "Your crew is together" })
              : t(`welcome_crew.tasks.${currentTask}.title`, { defaultValue: activeTask?.id || "Next task" })}
          </span>
        </span>
        <span className="rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-bold text-white/80">
          {completedCount}/{tasks.length}
        </span>
        <span className={`text-[16px] text-white/70 transition ${expanded ? "rotate-180" : ""}`}>⌄</span>
      </button>

      {expanded ? (
        <div className="border-t border-white/10 px-4 pb-4 pt-3">
          {currentTask !== "complete" ? (
            <>
              {activeTaskNeedsReward ? (
                <div className="flex items-center gap-3 rounded-[18px] border border-emerald-200/20 bg-emerald-300/10 p-2.5">
                  <span className="min-w-0 flex-1">
                    <span className="block text-[11px] font-semibold text-emerald-200">
                      {t("welcome_crew.task_complete", { defaultValue: "Mission complete" })}
                    </span>
                    <span className="mt-0.5 flex items-center gap-1 text-[14px] font-black text-white">
                      <TaskRewardValue reward={activeTask.reward} t={t} />
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => claimReward(activeTask)}
                    disabled={Boolean(claimingTaskId)}
                    className="shrink-0 rounded-full bg-white px-3 py-2 text-[11px] font-black text-blue-700 disabled:opacity-60"
                  >
                    {claimingTaskId === activeTask.id
                      ? t("welcome_crew.claiming_reward", { defaultValue: "Collecting…" })
                      : t("welcome_crew.claim_reward", { defaultValue: "Collect" })}
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-[13px] leading-[18px] text-white/80">
                    {t(`welcome_crew.tasks.${currentTask}.description`, {
                      time: departureOpenLabel,
                      defaultValue: "Complete this task in the crew chat.",
                    })}
                  </p>
                  {activeTask?.reward ? (
                    <div className="mt-2 inline-flex rounded-full bg-amber-300/15 px-3 py-1 text-[11px] font-bold text-amber-200">
                      <TaskRewardValue reward={activeTask.reward} t={t} />
                    </div>
                  ) : null}
                </>
              )}
              {state?.feedback?.code === "greeting_hint" && currentTask === "greeting" && !activeTaskNeedsReward ? (
                <p
                  role="status"
                  className="mt-2 rounded-[14px] border border-amber-200/20 bg-amber-200/10 px-3 py-2 text-[11px] font-semibold leading-[15px] text-amber-100"
                >
                  {t("welcome_crew.greeting_hint", {
                    defaultValue: "Start with a greeting — for example: “Hi! My name is Alex.”",
                  })}
                </p>
              ) : null}
            </>
          ) : null}

          <div className="mt-3 flex flex-wrap gap-2">
            {currentTask === "arrival_photo" && !activeTaskNeedsReward ? (
              <button type="button" onClick={onPickPhoto} className="rounded-full bg-white px-4 py-2 text-[12px] font-black text-blue-700">
                {t("welcome_crew.send_photo", { defaultValue: "Send arrival photo" })}
              </button>
            ) : null}
            {state?.qr_available ? (
              <button type="button" onClick={showQr} disabled={qrLoading} className="rounded-full bg-cyan-300 px-4 py-2 text-[12px] font-black text-[#061A35] disabled:opacity-60">
                {qrLoading
                  ? t("welcome_crew.qr_loading", { defaultValue: "Creating…" })
                  : t("welcome_crew.show_qr", { defaultValue: "Show my QR" })}
              </button>
            ) : null}
            {(currentTask === "arrival_photo" || currentTask === "meetup" || currentTask === "complete") && !activeTaskNeedsReward ? (
              state?.event?.attendance_confirmed ? (
                <button type="button" onClick={openSession} className="rounded-full border border-white/25 bg-white/10 px-4 py-2 text-[12px] font-bold text-white">
                  {t("welcome_crew.join_session", { defaultValue: "Join session" })}
                </button>
              ) : (
                <button type="button" onClick={openAttendance} className="rounded-full border border-white/25 bg-white/10 px-4 py-2 text-[12px] font-bold text-white">
                  {t("welcome_crew.confirm_attendance", { defaultValue: "Confirm attendance" })}
                </button>
              )
            ) : null}
          </div>

          {meetupMembers.length ? (
            <div className="mt-3">
              {confirmedMeetupCount ? (
                <p className="mb-1.5 text-[10px] font-black uppercase tracking-[0.12em] text-cyan-200">
                  {t("welcome_crew.arrived_members", { defaultValue: "At the venue" })} · {confirmedMeetupCount}/{meetupMembers.length}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-1.5">
                {meetupMembers.map((member) => (
                  <span key={member.user_id} className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${member.meetup_confirmed ? "bg-emerald-300/20 text-emerald-200" : "bg-white/10 text-white/60"}`}>
                    {member.meetup_confirmed ? "✓ " : ""}{member.code || member.username}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {state?.game?.unlocked && questionText ? (
            <div className="mt-3 rounded-[20px] bg-white/10 p-3">
              <p className="text-[10px] font-black uppercase tracking-[0.12em] text-cyan-200">
                {t("welcome_crew.game_title", { defaultValue: "Conversation starter" })}
              </p>
              <p className="mt-1 text-[15px] font-semibold leading-[20px] text-white">{questionText}</p>
              <button type="button" onClick={nextQuestion} disabled={gameLoading} className="mt-3 rounded-full bg-white px-4 py-2 text-[12px] font-black text-blue-700 disabled:opacity-60">
                {gameLoading
                  ? t("welcome_crew.game_loading", { defaultValue: "Loading…" })
                  : t("welcome_crew.game_next", { defaultValue: "Next question" })}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      <AppModalShell
        open={Boolean(qrPath)}
        onClose={() => setQrPath("")}
        title={t("welcome_crew.qr_title", { defaultValue: "My meetup QR" })}
        closeAriaLabel={t("common.close", { defaultValue: "Close" })}
        maxWidthClassName="max-w-[420px]"
        bodyClassName="overflow-hidden"
        forcePortalToBody
      >
        <div className="rounded-[24px] bg-white p-4 text-center shadow-[0_14px_34px_rgba(0,0,0,0.18)]">
          <canvas ref={qrCanvasRef} className="mx-auto h-auto w-full max-w-[280px]" />
          <p className="mx-auto mt-3 max-w-[290px] text-[13px] font-semibold leading-[18px] text-slate-600">
            {t("welcome_crew.qr_hint", { defaultValue: "Let a crew member scan this code. It refreshes after two minutes." })}
          </p>
        </div>
      </AppModalShell>
    </section>
  );
}
